"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArchivedLibraryEntryError = exports.workPackageVorbelegungSchema = exports.workPackageLibraryListInputSchema = exports.workPackageLibraryActionInputSchema = exports.workPackageLibraryUpdateInputSchema = exports.workPackageLibraryCreateInputSchema = exports.workPackageLibraryEntryListSchema = exports.workPackageLibraryEntrySchema = exports.gewerkListSchema = exports.gewerkSchema = exports.leistungsphasenSchema = exports.workPackageLibraryCodeSchema = exports.richtwertSchema = exports.WORK_PACKAGE_LIBRARY_BESCHREIBUNG_MAX_LENGTH = exports.WORK_PACKAGE_LIBRARY_CODE_MAX_LENGTH = exports.WORK_PACKAGE_LIBRARY_NAME_MAX_LENGTH = void 0;
exports.lphGruppeFuer = lphGruppeFuer;
exports.vorbelegungAusBibliothek = vorbelegungAusBibliothek;
const zod_1 = require("zod");
const common_js_1 = require("./common.js");
const tenant_settings_js_1 = require("./tenant-settings.js");
/**
 * Arbeitspaket-Bibliothek (AC-301).
 *
 * Transportfrei (ADR-04): nur Zod. Entscheidungen in
 * docs/stories/AC-301/ENTSCHEIDUNGEN.md; die Nummern (H1, E3 …) verweisen dorthin.
 *
 * Ersetzt `WorkPackageTemplate` aus AC-015 (H1): ein Stammarbeitspaket hängt an
 * einem GEWERK, nicht an einer Fachgruppe, und deckt eine Menge von
 * Leistungsphasen ab statt einer einzelnen (H2).
 *
 * Absichtlich NICHT hier: die normalisierte Bezeichnung für den
 * Dublettenvergleich. Sie entsteht ausschließlich in der Datenbank als
 * generierte Spalte (E3); eine zweite Normalisierung hier liefe irgendwann
 * auseinander.
 */
exports.WORK_PACKAGE_LIBRARY_NAME_MAX_LENGTH = 200;
exports.WORK_PACKAGE_LIBRARY_CODE_MAX_LENGTH = 40;
exports.WORK_PACKAGE_LIBRARY_BESCHREIBUNG_MAX_LENGTH = 2000;
/**
 * Richtwert in Stunden (E4): Zeichenkette mit höchstens einer Nachkommastelle
 * und größer als 0 — Stunden sind im Transport nie eine Zahl (CLAUDE.md §4
 * Regel 10). Die Obergrenze 9999,9 folgt aus `DECIMAL(5,1)`.
 *
 * Eine Vorbelegung, kein Plan (AK2): der Wert heißt in der Vorbelegung für
 * Instanzen `planHoursVorschlag` und fließt in keine Summe.
 */
exports.richtwertSchema = zod_1.z
    .string()
    .regex(/^\d{1,4}(\.\d)?$/, 'höchstens vier Stellen und eine Nachkommastelle')
    .refine((wert) => !/^0+(\.0)?$/.test(wert), 'muss größer als 0 sein');
/**
 * Fachlicher Code (E8), z. B. `BE01`: getrimmt und großgeschrieben. Die
 * Datenbank prüft dieselbe Form per CHECK; hier wird sie hergestellt, damit
 * eine kleingeschriebene Eingabe nicht als Fehler, sondern als derselbe Code
 * ankommt.
 */
exports.workPackageLibraryCodeSchema = (0, tenant_settings_js_1.anzeigetextSchema)(exports.WORK_PACKAGE_LIBRARY_CODE_MAX_LENGTH).transform((code) => code.toUpperCase());
/**
 * Die abgedeckten Leistungsphasen (H2): nicht leer, ohne Doppelte, aufsteigend
 * sortiert gespeichert. Ob die Menge genau einer Gruppe der
 * Mandantenkonfiguration entspricht, prüft der Service gegen den
 * gespeicherten Stand (`lphGruppeFuer`).
 */
exports.leistungsphasenSchema = zod_1.z
    .array(common_js_1.leistungsphaseSchema)
    .min(1)
    .max(9)
    .refine((phasen) => new Set(phasen).size === phasen.length, 'Leistungsphase doppelt')
    .transform((phasen) => [...phasen].sort((a, b) => a - b));
const nameSchema = (0, tenant_settings_js_1.anzeigetextSchema)(exports.WORK_PACKAGE_LIBRARY_NAME_MAX_LENGTH);
const beschreibungSchema = zod_1.z
    .string()
    .trim()
    .min(1)
    .max(exports.WORK_PACKAGE_LIBRARY_BESCHREIBUNG_MAX_LENGTH);
/** Ein Gewerk (E1), wie `workPackageLibrary.gewerke` es ausliefert. */
exports.gewerkSchema = zod_1.z.strictObject({
    id: common_js_1.uuidSchema,
    code: zod_1.z.string(),
    bezeichnung: zod_1.z.string(),
    bereich: zod_1.z.string(),
});
exports.gewerkListSchema = zod_1.z.array(exports.gewerkSchema);
/** Ein Stammarbeitspaket, wie die Bibliothek es ausliefert. */
exports.workPackageLibraryEntrySchema = zod_1.z.strictObject({
    id: common_js_1.uuidSchema,
    name: zod_1.z.string(),
    gewerkId: common_js_1.uuidSchema,
    leistungsphasen: zod_1.z.array(common_js_1.leistungsphaseSchema).min(1),
    richtwertH: exports.richtwertSchema.nullable(),
    code: zod_1.z.string().nullable(),
    beschreibung: zod_1.z.string().nullable(),
    archivedAt: common_js_1.timestampSchema.nullable(),
    version: zod_1.z.number().int().nonnegative(),
});
exports.workPackageLibraryEntryListSchema = zod_1.z.array(exports.workPackageLibraryEntrySchema);
/**
 * Anlegen (AK1): Bezeichnung, Gewerk und Leistungsphasen sind Pflicht.
 * Richtwert, Code und Beschreibung dürfen fehlen (AK2, E8).
 */
exports.workPackageLibraryCreateInputSchema = zod_1.z.strictObject({
    name: nameSchema,
    gewerkId: common_js_1.uuidSchema,
    leistungsphasen: exports.leistungsphasenSchema,
    richtwertH: exports.richtwertSchema.nullable().optional(),
    code: exports.workPackageLibraryCodeSchema.nullable().optional(),
    beschreibung: beschreibungSchema.nullable().optional(),
});
/**
 * Ändern: ersetzt den Eintrag vollständig. Die optionalen Felder sind hier
 * PFLICHT, `null` heißt „leeren" — ein weggelassenes Feld wäre zweideutig.
 * `version` ist die zuletzt gelesene Version (E11).
 */
exports.workPackageLibraryUpdateInputSchema = zod_1.z.strictObject({
    id: common_js_1.uuidSchema,
    version: zod_1.z.number().int().nonnegative(),
    name: nameSchema,
    gewerkId: common_js_1.uuidSchema,
    leistungsphasen: exports.leistungsphasenSchema,
    richtwertH: exports.richtwertSchema.nullable(),
    code: exports.workPackageLibraryCodeSchema.nullable(),
    beschreibung: beschreibungSchema.nullable(),
});
/** Archivieren oder Wiederherstellen (E6), mit der zuletzt gelesenen Version. */
exports.workPackageLibraryActionInputSchema = zod_1.z.strictObject({
    id: common_js_1.uuidSchema,
    version: zod_1.z.number().int().nonnegative(),
});
/** Filter der Liste. Archivierte Einträge nur auf ausdrücklichen Wunsch. */
exports.workPackageLibraryListInputSchema = zod_1.z.strictObject({
    gewerkId: common_js_1.uuidSchema.optional(),
    leistungsphasen: exports.leistungsphasenSchema.optional(),
    includeArchived: zod_1.z.boolean().optional(),
});
/**
 * Die Gruppe der Mandantenkonfiguration, der die Phasenmenge GENAU entspricht,
 * oder `null` (H2). Eine Teilmenge oder Obermenge einer Gruppe passt nicht.
 *
 * Für Service (Prüfung beim Schreiben) und Oberfläche (Gruppenanzeige,
 * Hinweis nach einer Umgruppierung) dieselbe Funktion.
 */
function lphGruppeFuer(phasen, gruppen) {
    const gesucht = new Set(phasen);
    return (gruppen.find((gruppe) => gruppe.phases.length === gesucht.size && gruppe.phases.every((phase) => gesucht.has(phase))) ?? null);
}
// --- Vorbelegung für Instanzen (E5) ------------------------------------------------
/**
 * Was eine Projektinstanz aus einem Bibliothekseintrag übernimmt (E5, AK4).
 *
 * Ein SCHNAPPSCHUSS: die Instanz kopiert die Werte beim Erzeugen und hält
 * `libraryId` nur als Herkunft. Spätere Änderungen in der Bibliothek erreichen
 * bestehende Instanzen deshalb nie — es gibt keinen Weg, auf dem eine Instanz
 * live aus der Bibliothek läse.
 *
 * Der Richtwert heißt hier `planHoursVorschlag` und ausdrücklich NICHT
 * `planHours` (AK2): er ist eine Vorbelegung, die die Projektleitung beim
 * Instanziieren übernimmt oder überschreibt, kein verbindlicher Plan.
 *
 * Nicht übernommen werden `code` und `beschreibung` (die Instanz hat dafür
 * keine Felder) sowie Zuständige — die gibt es nur auf Instanzebene.
 */
exports.workPackageVorbelegungSchema = zod_1.z.strictObject({
    libraryId: common_js_1.uuidSchema,
    name: zod_1.z.string(),
    gewerkId: common_js_1.uuidSchema,
    leistungsphasen: zod_1.z.array(common_js_1.leistungsphaseSchema).min(1),
    planHoursVorschlag: exports.richtwertSchema.nullable(),
});
/** Ein archivierter Eintrag ist für neue Instanzen nicht wählbar (E6). */
class ArchivedLibraryEntryError extends Error {
    libraryId;
    constructor(libraryId) {
        super('Ein archiviertes Stammarbeitspaket ist für neue Instanzen nicht wählbar (AC-301, E6).');
        this.libraryId = libraryId;
        this.name = 'ArchivedLibraryEntryError';
    }
}
exports.ArchivedLibraryEntryError = ArchivedLibraryEntryError;
/**
 * Die EINZIGE Überführung eines Bibliothekseintrags in die Vorbelegung einer
 * Instanz (E5). AC-303 erzeugt Instanzen ausschließlich hierüber.
 *
 * Gibt ein neues Objekt mit eigener Kopie der Leistungsphasen zurück: eine
 * spätere Änderung am Eintrag — auch eine Mutation desselben Objekts im
 * Speicher — erreicht die Vorbelegung nicht.
 */
function vorbelegungAusBibliothek(eintrag) {
    if (eintrag.archivedAt !== null) {
        throw new ArchivedLibraryEntryError(eintrag.id);
    }
    return {
        libraryId: eintrag.id,
        name: eintrag.name,
        gewerkId: eintrag.gewerkId,
        leistungsphasen: [...eintrag.leistungsphasen],
        planHoursVorschlag: eintrag.richtwertH,
    };
}
//# sourceMappingURL=work-package-library.js.map